import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useI18n } from '../../lib/i18n';
import { Badge, Button, Card, errorText, useToast } from '../../ui/components';

interface Result {
  person: { id: string; name: string; memberNumber: string | null; photoUrl: string | null; stage: string; healthAttention: boolean };
  checkedIn: boolean;
  booking: { lesson: string; starts_at: string; location: string; room: string | null; status: string } | null;
  upcoming: { lesson: string; starts_at: string }[];
  tokens: { name: string; available: number }[];
  membershipStatus: string | null;
  outstandingCents: number;
}

/**
 * Full-screen check-in screen for the front desk. Members type or scan their code; barcode scanners
 * type the code and press Enter, so no camera is needed.
 */
export function KioskPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!result && !error) return undefined;
    const id = setTimeout(() => { setResult(null); setError(''); input.current?.focus(); }, 8000);
    return () => clearTimeout(id);
  }, [result, error]);

  const submit = async (): Promise<void> => {
    const value = code.trim();
    if (!value || busy) return;
    setBusy(true);
    setCode('');
    try {
      setResult(await api<Result>(`${T(gym.slug)}/admin/check-in`, { body: { code: value } }));
      setError('');
    } catch (e) {
      setResult(null);
      setError(e instanceof ApiError && e.status === 404 ? i18n.t('kiosk.unknown_code') : errorText(e, i18n));
    } finally {
      setBusy(false);
      input.current?.focus();
    }
  };

  return (
    <div className="kiosk">
      <div className="kiosk-head">
        <strong>{gym.gym.name}</strong>
        <Button variant="ghost" size="sm" onClick={() => window.history.back()}>{i18n.t('common.close')}</Button>
      </div>
      <form className="kiosk-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <h1>{i18n.t('kiosk.title')}</h1>
        <p className="muted">{i18n.t('kiosk.hint')}</p>
        <input ref={input} autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={i18n.t('kiosk.placeholder')} className="input kiosk-input" autoComplete="off" spellCheck={false} aria-label={i18n.t('kiosk.placeholder')} />
        <Button type="submit" size="lg" busy={busy} disabled={!code.trim()}>{i18n.t('kiosk.check_in')}</Button>
      </form>

      {error && <Card className="kiosk-result bad"><h2>{error}</h2></Card>}
      {result && (
        <Card className={`kiosk-result ${result.checkedIn ? 'good' : 'warn'}`}>
          <div className="row between">
            <h2>{result.checkedIn ? `✓ ${i18n.t('kiosk.welcome', { name: result.person.name })}` : result.person.name}</h2>
            {result.person.memberNumber && <span className="muted">#{result.person.memberNumber}</span>}
          </div>
          {result.booking ? (
            <p>{result.booking.lesson} · {i18n.time(result.booking.starts_at)} · {[result.booking.location, result.booking.room].filter(Boolean).join(' · ')}</p>
          ) : (
            <p className="warn">{i18n.t('kiosk.no_booking')}</p>
          )}
          <div className="row gap-sm wrap">
            {result.membershipStatus && <Badge value={result.membershipStatus} />}
            {result.person.healthAttention && <span className="badge badge-bad">⚕ {i18n.t('attendance.health')}</span>}
            {result.outstandingCents > 0 && <span className="badge badge-warn">{i18n.t('crm.outstanding', { amount: i18n.money(result.outstandingCents) })}</span>}
            {result.tokens.filter((t) => t.available > 0).map((t) => <span key={t.name} className="badge badge-neutral">{t.available}× {t.name}</span>)}
          </div>
          {result.upcoming.length > 0 && (
            <p className="small muted">{i18n.t('kiosk.next')}: {result.upcoming.slice(0, 2).map((u) => `${u.lesson} ${i18n.dateTime(u.starts_at)}`).join(' · ')}</p>
          )}
          {!result.booking && (
            <Button variant="secondary" onClick={() => { setResult(null); toast.ok(i18n.t('kiosk.sent_to_planner')); window.open(`${gym.base}/admin/planner`, '_blank'); }}>
              {i18n.t('nav.planner')}
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}
